'use client';

/**
 * Live insets for the "teacher-goal" reference screen (canvas 1672×941).
 *
 * The five goal rows on the left become live controls (selected title lime,
 * others muted) with the lime bar travelling between them; the target label
 * on the right and the "Почему этот маршрут" strip follow `teacherGoal`; the
 * chosen chip on each of the four axes gets a ring. The painted route curve,
 * the axis icons and the lab stay untouched.
 */

import { Fragment, useRef, type CSSProperties } from 'react';
import { rectStyle, SCENE_CANVAS, type Rect } from '../../types';
import type { InsetProps } from '../index';
import styles from './teacher-goal.module.css';

const C = SCENE_CANVAS['teacher-goal'];

const GOAL_Y = [329, 418, 505, 592, 678] as const;
const ROW_W = 335;
const ROW_H = 70;
const ROW_X = 140;
const ROW_DY = 6;
/** Sampled per-row panel tone (the left column darkens slightly downwards). */
const ROW_BG = ['#040c14', '#020a12', '#020912', '#02080f', '#02080f'] as const;
const BAR: Rect = [58, 329, 3, 83];
/**
 * Centred on the painted label (text 1480..1623 × 358..453). The cover is a
 * little larger than the text and its edges are feathered in CSS, so it
 * blends into the formula board instead of reading as a rectangle.
 */
const TARGET: Rect = [1430, 332, 242, 142];
const STRIP: Rect = [64, 806, 1190, 84];

const AXIS_X = [584, 765, 1058, 1266] as const;
const AXIS_W = [170, 170, 170, 200] as const;
const AXIS_Y = [
  [374, 431, 488],
  [331, 389, 447],
  [309, 367, 425],
  [278, 335, 393],
] as const;
const CHIP_H = 47;
/** The artist painted the choice of goal 0 in lime: index 1 on every axis. */
const PAINTED_CHOICE = [1, 1, 1, 1] as const;
/** Bounding boxes of the painted lime chip highlights (for the mute veils). */
const PAINTED_CHIP: readonly Rect[] = [
  [580, 429, 158, 49],
  [757, 400, 120, 27],
  [1054, 365, 175, 44],
  [1262, 333, 146, 40],
];
/** Goal → chosen index on [horizon, diagnostic depth, task format, hint pattern]. */
const CHOICE: readonly (readonly [number, number, number, number])[] = [
  [1, 1, 1, 1],
  [1, 0, 1, 0],
  [1, 1, 2, 1],
  [2, 1, 2, 2],
  [2, 2, 1, 2],
];

const AXIS_CHOICES = [
  [['Короткий', 'Short'], ['Средний', 'Medium'], ['Длинный', 'Long']],
  [['Базовая', 'Basic'], ['Углублённая', 'Deep'], ['Экспертная', 'Expert']],
  [['Тренировочные', 'Practice'], ['Ситуационные', 'Situational'], ['Исследовательские', 'Inquiry']],
  [['Прямые подсказки', 'Direct hints'], ['Наводящие вопросы', 'Guiding questions'], ['Открытые ориентиры', 'Open cues']],
] as const;
const AXIS_NAMES = [
  ['Горизонт обучения', 'Learning horizon'],
  ['Глубина диагностики', 'Diagnostic depth'],
  ['Формат заданий', 'Task format'],
  ['Паттерн рекомендаций', 'Hint pattern'],
] as const;

interface GoalCopy {
  title: { ru: string; en: string };
  note: { ru: string; en: string };
  why: { ru: string; en: string };
}

const GOALS: readonly GoalCopy[] = [
  {
    title: { ru: 'Понятие и смысл', en: 'Meaning and understanding' },
    note: { ru: 'Глубокое понимание ключевых идей', en: 'Deep understanding of key ideas' },
    why: { ru: 'он ведёт к глубокому пониманию через постепенное усложнение и осмысленные задачи. Диагностика помогает точно видеть прогресс, а рекомендации развивают самостоятельное мышление.', en: 'it leads to deep understanding through gradual complexity and meaningful tasks. Diagnostics show progress precisely, and the hints develop independent thinking.' },
  },
  {
    title: { ru: 'Применение на практике', en: 'Apply in practice' },
    note: { ru: 'Уверенное применение знаний', en: 'Confident application of knowledge' },
    why: { ru: 'он закрепляет знания в типовых и ситуационных задачах на среднем горизонте. Базовая диагностика следит за ошибками, а прямые подсказки не дают застрять.', en: 'it consolidates knowledge in routine and situational tasks over a medium horizon. Basic diagnostics track errors, and direct hints keep the learner from getting stuck.' },
  },
  {
    title: { ru: 'Анализ и оценка', en: 'Analysis and evaluation' },
    note: { ru: 'Критическое мышление и аргументация', en: 'Critical thinking and reasoning' },
    why: { ru: 'он учит сравнивать способы решения и обосновывать выбор. Углублённая диагностика показывает ход рассуждения, а наводящие вопросы ведут к самостоятельным выводам.', en: 'it teaches comparing solution methods and justifying the choice. Deep diagnostics reveal the reasoning, and guiding questions lead to independent conclusions.' },
  },
  {
    title: { ru: 'Исследование и творчество', en: 'Inquiry and creativity' },
    note: { ru: 'Самостоятельный поиск и открытия', en: 'Independent search and discovery' },
    why: { ru: 'он даёт длинный горизонт и открытые задачи, где ответ не задан заранее. Диагностика отслеживает гипотезы, а ориентиры оставляют место для собственного пути.', en: 'it offers a long horizon and open tasks with no preset answer. Diagnostics track hypotheses, and open cues leave room for the learner’s own path.' },
  },
  {
    title: { ru: 'Мастерство и перенос', en: 'Mastery and transfer' },
    note: { ru: 'Устойчивые навыки и перенос знаний', en: 'Durable skills and transfer of knowledge' },
    why: { ru: 'он доводит навык до устойчивости и переносит его в новые контексты. Экспертная диагностика ловит тонкие ошибки, а открытые ориентиры поддерживают самостоятельность.', en: 'it takes the skill to durability and transfers it into new contexts. Expert diagnostics catch subtle errors, and open cues sustain independence.' },
  },
];

const T = {
  why: { ru: 'Почему этот маршрут:', en: 'Why this route:' },
  goal: { ru: 'Педагогическая цель', en: 'Teacher goal' },
  chosen: { ru: 'выбрано', en: 'chosen' },
} as const;

function useChanged(value: unknown): boolean {
  const initial = useRef(value);
  const changed = useRef(false);
  if (!Object.is(value, initial.current)) changed.current = true;
  return changed.current;
}

function BulbIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M8.5 17.5a6.5 6.5 0 1 1 7 0v1.8a1 1 0 0 1-1 1h-5a1 1 0 0 1-1-1z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M10 22h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export default function TeacherGoalInsets({ lang, state, update, spotlight, onInteraction }: InsetProps) {
  const goal = Math.min(GOALS.length - 1, Math.max(0, state.teacherGoal));
  const copy = GOALS[goal];
  const choice = CHOICE[goal];
  const live = useChanged(goal);
  const pick = <V,>(pair: { ru: V; en: V }): V => (lang === 'ru' ? pair.ru : pair.en);
  const ru = lang === 'ru';

  const barStyle: CSSProperties & Record<'--dy', string> = {
    ...rectStyle(BAR, C),
    '--dy': `${((GOAL_Y[goal] - GOAL_Y[0]) / C.width) * 100}`,
  };

  return (
    <div className={styles.root}>
      <i className={styles.bar} style={barStyle} data-live={live || undefined} aria-hidden="true" />

      <div className={styles.goals} role="radiogroup" aria-label={pick(T.goal)}>
        {GOALS.map((item, index) => {
          const selected = index === goal;
          const id = `goal-${index}`;
          const spot = spotlight === id;
          const label = ru ? item.title.ru : `${T.goal.en}: ${item.title.en}`;
          return (
            <button
              key={id}
              type="button"
              role="radio"
              className={styles.goal}
              style={{ ...rectStyle([ROW_X, GOAL_Y[index] + ROW_DY, ROW_W, ROW_H], C), background: ROW_BG[index] }}
              data-hotspot={id}
              data-selected={selected ? 'true' : undefined}
              data-spotlight={spot ? 'true' : undefined}
              aria-checked={selected}
              aria-label={label}
              onClick={() => {
                onInteraction?.(label);
                update({ teacherGoal: index });
              }}
            >
              <b>{pick(item.title)}</b>
              <small>{pick(item.note)}</small>
              {spot && <i className={styles.pulse} aria-hidden="true" />}
            </button>
          );
        })}
      </div>

      {AXIS_X.map((x, axis) => {
        const chosen = choice[axis];
        const y = AXIS_Y[axis][chosen];
        const veilOn = chosen !== PAINTED_CHOICE[axis];
        return (
          <Fragment key={axis}>
            <i
              className={styles.chipVeil}
              style={rectStyle(PAINTED_CHIP[axis], C)}
              data-on={veilOn ? 'true' : undefined}
              aria-hidden="true"
            />
            <i
              key={`chip-${axis}-${chosen}`}
              className={styles.chipRing}
              style={rectStyle([x + 2, y + 4, AXIS_W[axis] - 4, CHIP_H - 8], C)}
              data-live={live || undefined}
              role="img"
              aria-label={`${pick({ ru: AXIS_NAMES[axis][0], en: AXIS_NAMES[axis][1] })}: ${ru ? AXIS_CHOICES[axis][chosen][0] : AXIS_CHOICES[axis][chosen][1]} (${pick(T.chosen)})`}
            />
          </Fragment>
        );
      })}

      <div className={styles.target} style={rectStyle(TARGET, C)} data-live={live || undefined} aria-hidden="true">
        <div key={goal} className={styles.targetBody}>
          <b style={{ '--i': 0 } as CSSProperties}>{pick(copy.title)}</b>
          <small style={{ '--i': 1 } as CSSProperties}>{pick(copy.note)}</small>
        </div>
      </div>

      <section className={styles.strip} style={rectStyle(STRIP, C)} data-live={live || undefined} aria-label={pick(T.why).replace(':', '')}>
        <span className={styles.stripIcon}><BulbIcon /></span>
        <p key={goal} className={styles.stripText}>
          <b>{pick(T.why)}</b> {pick(copy.why)}
        </p>
        <i key={`relight-${goal}`} className={styles.relight} aria-hidden="true" />
      </section>
    </div>
  );
}
